import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Sparkles } from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackingField = ({ label, hint, idKey, enabledKey, placeholder, s, setS }) => (
  <div className="rounded-2xl border border-white/12 bg-[#4E5A3B]/50 p-5">
    <div className="flex items-center justify-between mb-3">
      <div>
        <div className="font-display font-black text-[#F3F1E8]">{label}</div>
        {hint && <div className="text-[11px] text-[#A8A58F] mt-0.5">{hint}</div>}
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!s[enabledKey]} onChange={e => setS({ ...s, [enabledKey]: e.target.checked })} data-testid={`toggle-${enabledKey}`} />
        <span className="text-xs text-[#C9C6B4] font-bold">{s[enabledKey] ? "AN" : "AUS"}</span>
      </label>
    </div>
    <input
      type="text"
      value={s[idKey] || ""}
      onChange={e => setS({ ...s, [idKey]: e.target.value.trim() })}
      placeholder={placeholder}
      className="w-full bg-[#5C6B49] border border-white/15 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] placeholder:text-[#A8A58F] outline-none focus:border-[#E8E5D0]"
      data-testid={`input-${idKey}`}
    />
  </div>
);

const AdminSettings = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(() => axios.create({
    baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);

  const [s, setS] = useState({
    gtm_id: "", gtm_enabled: false,
    ga4_id: "", ga4_enabled: false,
    meta_pixel_id: "", meta_pixel_enabled: false,
    tiktok_pixel_id: "", tiktok_enabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "CRM · Tracking";
    if (!token) { navigate("/admin/login"); return; }
    setLoading(true);
    api.get("/tracking/settings").then(r => setS(prev => ({ ...prev, ...r.data })))
      .catch(e => { if ([401, 403].includes(e?.response?.status)) navigate("/admin/login"); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tracking/settings", s);
      toast.success("Tracking-Einstellungen gespeichert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-settings-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-2">
              <ArrowLeft size={12}/> CRM
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Tracking & Integrationen</h1>
            <p className="text-[#A8A58F] text-sm mt-1">IDs eintragen und aktivieren. Tracking lädt erst nach Cookie-Einwilligung.</p>
          </div>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-full px-5 py-2.5 text-sm disabled:opacity-50" data-testid="save-settings-btn">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Speichern
          </button>
        </div>

        {loading ? <div className="text-[#C9C6B4]">Lade…</div> : (
          <div className="space-y-4">
            <TrackingField s={s} setS={setS} label="Google Tag Manager" hint="Container-ID (z. B. GTM-XXXXXXX)" idKey="gtm_id" enabledKey="gtm_enabled" placeholder="GTM-XXXXXXX" />
            <TrackingField s={s} setS={setS} label="Google Analytics 4" hint="Measurement-ID (z. B. G-XXXXXXXXXX)" idKey="ga4_id" enabledKey="ga4_enabled" placeholder="G-XXXXXXXXXX" />
            <TrackingField s={s} setS={setS} label="Meta Pixel" hint="Pixel-ID (numerisch)" idKey="meta_pixel_id" enabledKey="meta_pixel_enabled" placeholder="1234567890" />
            <TrackingField s={s} setS={setS} label="TikTok Pixel" hint="Pixel-ID" idKey="tiktok_pixel_id" enabledKey="tiktok_enabled" placeholder="C1234567ABCDEFG" />
            <div className="rounded-2xl border border-[#E8E5D0]/25 bg-[#E8E5D0]/[0.06] p-4 flex items-start gap-3 text-sm">
              <Sparkles size={16} className="text-[#E8E5D0] shrink-0 mt-0.5" />
              <div className="text-[#E5E2D0]">
                <strong className="text-[#F3F1E8]">Meta Conversions API, Google Ads Enhanced/Offline Conversions</strong> sind
                als Backend-Struktur vorbereitet (Phase 4). Zur Aktivierung werden Access-Tokens benötigt.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
